// FILE: src/questionTypes/coreTypes.js
//
// Phase-1 question-type definitions (logic only — renderer components are
// wired in PR 2 (AdminEditor) and PR 3 (LearnerRenderer/ReviewRenderer)).
// Scoring is all-or-nothing for every Phase-1 type, mirroring
// _case_score_answer in supabase/migrations/071_case_study_rpcs.sql.
// Shared fixtures: __fixtures__/scoringVectors.json.
//
// Answer shapes (canonical, post-normalizeAnswer):
//   multiple_choice / visual_interpretation: ['B']
//   multiple_select:                          ['A','C']            (set)
//   enhanced_matching:                        ['p1:o2','p2:o1']    (set of pairs)
//   ordered_sequence:                         ['s3','s1','s2']     (exact order)

import {
  registerQuestionType,
  normalizeAnswerArray,
  setsEqual,
  arraysEqual,
} from './registry';

function keyedAnswers(question) {
  return normalizeAnswerArray(question && question.correctAnswers);
}

function choiceCount(question) {
  return Array.isArray(question && question.choices) ? question.choices.length : 0;
}

// --- multiple_choice (single-select radio) ----------------------------------
registerQuestionType({
  type: 'multiple_choice',
  supported: true,
  validate(q) {
    const errors = [];
    if (choiceCount(q) < 2) errors.push('Needs at least 2 options.');
    if (keyedAnswers(q).length !== 1) errors.push('Must key exactly 1 correct answer.');
    return errors;
  },
  normalizeAnswer: normalizeAnswerArray,
  score(q, a) {
    return setsEqual(normalizeAnswerArray(a), keyedAnswers(q));
  },
});

// --- multiple_select (checkboxes) -------------------------------------------
registerQuestionType({
  type: 'multiple_select',
  supported: true,
  validate(q) {
    const errors = [];
    if (choiceCount(q) < 4) errors.push('Needs at least 4 options.');
    if (keyedAnswers(q).length < 2) errors.push('Must key at least 2 correct answers.');
    return errors;
  },
  normalizeAnswer: normalizeAnswerArray,
  score(q, a) {
    return setsEqual(normalizeAnswerArray(a), keyedAnswers(q));
  },
});

// --- visual_interpretation (exhibit image + single-select) ------------------
registerQuestionType({
  type: 'visual_interpretation',
  supported: true,
  validate(q) {
    const errors = [];
    if (choiceCount(q) < 2) errors.push('Needs at least 2 options.');
    if (keyedAnswers(q).length !== 1) errors.push('Must key exactly 1 correct answer.');
    if (!q || !q.image || !q.image.src) errors.push('Requires an exhibit image.');
    if (q && q.image && q.image.src && !q.image.alt) errors.push('Exhibit image needs alt text.');
    return errors;
  },
  normalizeAnswer: normalizeAnswerArray,
  score(q, a) {
    return setsEqual(normalizeAnswerArray(a), keyedAnswers(q));
  },
});

// --- enhanced_matching -------------------------------------------------------
// choices jsonb shape: { prompts: [{id, text}], options: [{id, text}] }
// key + answers encode as 'promptId:optionId' pair strings (set semantics).
// Accessibility: learner renderer (PR 3) uses one labelled <select> per
// prompt row — no dragging required.
registerQuestionType({
  type: 'enhanced_matching',
  supported: true,
  validate(q) {
    const errors = [];
    const c = (q && q.choices) || {};
    const prompts = Array.isArray(c.prompts) ? c.prompts : [];
    const options = Array.isArray(c.options) ? c.options : [];
    const key = keyedAnswers(q);
    if (prompts.length < 2) errors.push('Needs at least 2 prompts to match.');
    if (options.length < 2) errors.push('Needs at least 2 options to match against.');
    if (key.length !== prompts.length) {
      errors.push('The key must map every prompt exactly once.');
    }
    if (key.some((pair) => !/^[^:]+:[^:]+$/.test(pair))) {
      errors.push("Key entries must be 'promptId:optionId' pairs.");
    }
    const keyedPrompts = key.map((p) => p.split(':')[0]);
    if (new Set(keyedPrompts).size !== keyedPrompts.length) {
      errors.push('Each prompt may be keyed only once.');
    }
    return errors;
  },
  normalizeAnswer: normalizeAnswerArray,
  score(q, a) {
    return setsEqual(normalizeAnswerArray(a), keyedAnswers(q));
  },
});

// --- ordered_sequence --------------------------------------------------------
// choices jsonb shape: { items: [{id, text}] } presented shuffled; the key is
// the correct ordering of item ids. Order matters when scoring.
// Accessibility: learner renderer (PR 3) uses keyboard Up/Down buttons as the
// primary interaction — dragging is optional sugar, never required.
registerQuestionType({
  type: 'ordered_sequence',
  supported: true,
  validate(q) {
    const errors = [];
    const c = (q && q.choices) || {};
    const items = Array.isArray(c.items) ? c.items : [];
    const key = keyedAnswers(q);
    if (items.length < 2) errors.push('Needs at least 2 items to order.');
    if (key.length !== items.length) errors.push('The key must order every item exactly once.');
    const ids = items.map((i) => String(i.id));
    if (key.length === items.length && !setsEqual(key, ids)) {
      errors.push('The key must be a permutation of the item ids.');
    }
    return errors;
  },
  normalizeAnswer: normalizeAnswerArray,
  score(q, a) {
    return arraysEqual(normalizeAnswerArray(a), keyedAnswers(q));
  },
});

// --- Future types: registered as UNSUPPORTED stubs. validate_case_study
// (migration 071) and the admin validation gate block publishing questions
// of these types until a real renderer lands. Never fake an interaction.
['dropdown_completion', 'point_and_click', 'graphic_based', 'hotspot'].forEach(
  (type) =>
    registerQuestionType({
      type,
      supported: false,
      validate: () => [
        `Question type "${type}" has no supported renderer yet and cannot be published.`,
      ],
      score: () => false,
    })
);
